import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Check, X } from "lucide-react";
import { LANG_NAMES } from "@/lib/i18n";
import { LANGS } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

// sppath: build SPA path from lang + slug (no trailing slash for router)
export function spath(lang, slug) {
  const s = (slug || "").replace(/^\/+|\/+$/g, "");
  return s === "" ? `/${lang}` : `/${lang}/${s}`;
}

export default function LanguageSwitcher({ lang }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { switcher } = useSite();
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Fallback switcher = home links for each language
  const items = switcher || LANGS.map((l) => ({ lang: l, name: LANG_NAMES[l], available: true, current: l === lang, url: `/${l}` }));

  const go = (item) => {
    if (!item.available || !item.url) return;
    // language change stays in-language via entity slug map
    navigate(item.url.replace(/\/$/, "") || `/${item.lang}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-sm text-ink/90 hover:text-copper transition-colors uppercase tracking-wide"
        aria-haspopup="listbox" aria-expanded={open}
      >
        <Globe size={16} /> {lang}
      </button>
      {open && (
        <ul
          data-testid="language-switcher-menu"
          className="absolute right-0 rtl:right-auto rtl:left-0 mt-3 w-52 bg-graphite border border-white/10 rounded-lg shadow-xl overflow-hidden z-50"
          role="listbox"
        >
          {items.filter((item) => item.available).map((item) => (
            <li key={item.lang}>
              <button
                data-testid={`language-option-${item.lang}`}
                onClick={() => go(item)}
                disabled={!item.available}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                  item.available ? "hover:bg-surface text-ink" : "text-faint cursor-not-allowed"
                } ${item.current ? "text-copper" : ""}`}
                title={item.available ? "" : "unavailable"}
              >
                <span>{item.name}</span>
                {item.current ? <Check size={14} /> : (!item.available && <X size={13} />)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
